import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';

const createId = () =>
  `effort_${Date.now()}${Math.random().toString(36).slice(2, 8)}`;

const TemplateEditor = ({ templates, setTemplates, onClose }) => {
  const [name, setName] = useState('');
  const [efforts, setEfforts] = useState([]);

  const addEffort = () => {
    setEfforts((currentEfforts) => [
      ...currentEfforts,
      {
        id: createId(),
        name: '',
        freq: 1,
        freqUnit: 'week',
        time: 30,
        timeUnit: 'min'
      }
    ]);
  };

  const updateEffort = (id, field, value) => {
    setEfforts((currentEfforts) =>
      currentEfforts.map((effort) =>
        effort.id === id ? { ...effort, [field]: value } : effort
      )
    );
  };

  const saveTemplate = (event) => {
    event.preventDefault();

    if (!name.trim()) {
      return;
    }

    setTemplates((currentTemplates) => [
      ...currentTemplates,
      {
        id: `template_${Date.now()}`,
        name: name.trim(),
        efforts
      }
    ]);

    setName('');
    setEfforts([]);
  };

  const deleteTemplate = (id) => {
    setTemplates((currentTemplates) =>
      currentTemplates.filter((template) => template.id !== id)
    );
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Vorlagen-Einstellungen
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Aufwände speichern und später per Drag & Drop auswählen
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Vorlagen-Einstellungen schließen"
          >
            <SafeIcon name="X" className="text-xl" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-8">
          <form
            onSubmit={saveTemplate}
            className="bg-gray-50 border border-gray-200 rounded-xl p-5"
          >
            <h3 className="font-bold text-gray-900 mb-4">
              Neue Vorlage anlegen
            </h3>

            <label className="block text-sm font-medium text-gray-600">
              Vorlagenname
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="z. B. Rückfragen"
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500"
                required
              />
            </label>

            <div className="mt-5 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-800">
                  Aufwandsschritte
                </h4>
                <button
                  type="button"
                  onClick={addEffort}
                  className="text-sm font-semibold text-red-600 hover:text-red-700"
                >
                  + Schritt hinzufügen
                </button>
              </div>

              {efforts.map((effort) => (
                <div
                  key={effort.id}
                  className="grid grid-cols-2 md:grid-cols-5 gap-2"
                >
                  <input
                    value={effort.name}
                    onChange={(event) =>
                      updateEffort(effort.id, 'name', event.target.value)
                    }
                    placeholder="Tätigkeit"
                    className="md:col-span-2 border border-gray-300 rounded-lg px-2 py-2 text-sm"
                  />
                  <input
                    type="number"
                    value={effort.freq}
                    onChange={(event) =>
                      updateEffort(effort.id, 'freq', event.target.value)
                    }
                    placeholder="Anzahl"
                    className="border border-gray-300 rounded-lg px-2 py-2 text-sm"
                  />
                  <select
                    value={effort.freqUnit}
                    onChange={(event) =>
                      updateEffort(effort.id, 'freqUnit', event.target.value)
                    }
                    className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white"
                  >
                    <option value="day">pro Tag</option>
                    <option value="week">pro Woche</option>
                    <option value="month">pro Monat</option>
                    <option value="year">pro Jahr</option>
                  </select>
                  <input
                    type="number"
                    value={effort.time}
                    onChange={(event) =>
                      updateEffort(effort.id, 'time', event.target.value)
                    }
                    placeholder="Minuten"
                    className="border border-gray-300 rounded-lg px-2 py-2 text-sm"
                  />
                </div>
              ))}
            </div>

            <button
              type="submit"
              className="mt-5 bg-red-600 hover:bg-red-700 text-white rounded-lg px-5 py-2.5 text-sm font-bold transition-colors"
            >
              Vorlage speichern
            </button>
          </form>

          <div>
            <h3 className="font-bold text-gray-900 mb-3">
              Vorhandene Vorlagen
            </h3>

            {templates.length === 0 ? (
              <p className="text-sm text-gray-500">
                Noch keine Vorlagen angelegt.
              </p>
            ) : (
              <div className="space-y-2">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3"
                  >
                    <div>
                      <p className="font-semibold text-gray-800">
                        {template.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {template.efforts.length} Aufwandsschritte
                      </p>
                    </div>
                    <button
                      onClick={() => deleteTemplate(template.id)}
                      className="text-gray-400 hover:text-red-600"
                      aria-label={`${template.name} löschen`}
                    >
                      <SafeIcon name="Trash2" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="p-5 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="bg-gray-900 hover:bg-black text-white rounded-lg px-6 py-2.5 text-sm font-semibold"
          >
            Einstellungen schließen
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;